import { InjectionToken, Signal, signal } from '@angular/core';
import { EMPTY, Observable } from 'rxjs';

/**
 * Lib-side abstraction over the SSE stream used by `<tf-comments>` to react to remote
 * create/edit/delete/restore events on the same target. The lib itself doesn't depend on the host
 * app's `SseV2Service`; the host provides an implementation via {@link COMMENTS_EVENTS}, and the
 * lib defaults to a silent stream so component-level usage in tests and isolated dev hosts keeps
 * working without SSE wiring.
 *
 * Hybrid handling (see `docs/plans/comments/frontend_plan.md` §F5): CREATED is a thin notification
 * (consumer refetches `GET /api/v2/comments/{id}` to pick up resolved author + caller-scoped
 * `is_unread`). EDITED / DELETED / RESTORED carry enough payload to patch in place.
 */
export type CommentEventKind =
  | 'COMMENT_CREATED:V2'
  | 'COMMENT_EDITED:V2'
  | 'COMMENT_DELETED:V2'
  | 'COMMENT_RESTORED:V2'
  | 'COMMENT_REACTED:V2'
  | 'COMMENT_UNREACTED:V2';

export type CommentsEventsConnectionState =
  | 'disconnected'
  | 'connecting'
  | 'connected'
  | 'reconnecting';

interface CommentEventBase {
  readonly kind: CommentEventKind;
  readonly commentId: string;
  readonly targetType: string;
  readonly targetId: string;
}

export interface CommentCreatedEvent extends CommentEventBase {
  readonly kind: 'COMMENT_CREATED:V2';
  readonly parentCommentId: string | null;
  readonly rootCommentId: string;
  readonly authorId: string;
  readonly bodyMarkdown: string;
  readonly createdAt: string;
}

export interface CommentEditedEvent extends CommentEventBase {
  readonly kind: 'COMMENT_EDITED:V2';
  readonly authorId: string;
  readonly editorId: string;
  readonly bodyMarkdown: string;
  readonly lastEditedAt: string;
}

export interface CommentDeletedEvent extends CommentEventBase {
  readonly kind: 'COMMENT_DELETED:V2';
  readonly authorId: string;
  readonly deletedBy: string;
  readonly deletedAt: string;
}

export interface CommentRestoredEvent extends CommentEventBase {
  readonly kind: 'COMMENT_RESTORED:V2';
  readonly authorId: string;
  readonly restoredBy: string;
  readonly bodyMarkdown: string;
  readonly restoredAt: string;
}

/**
 * Phase 6a reaction events: the broker payload is a delta (single emoji + reactor), not the full
 * aggregate. Subscribers recompute the local `reactions[]` from the delta. The aggregate is small
 * and the local copy is already in memory, so a per-event full-array push would waste bandwidth.
 *
 * `commentAuthorId` is carried for notification routing (so a notifications consumer can dispatch
 * to the comment's author without round-tripping for the row); the comments component itself
 * ignores it.
 */
export interface CommentReactedEvent extends CommentEventBase {
  readonly kind: 'COMMENT_REACTED:V2';
  readonly commentAuthorId: string;
  readonly reactorId: string;
  readonly emoji: string;
  readonly occurredAt: string;
}

export interface CommentUnreactedEvent extends CommentEventBase {
  readonly kind: 'COMMENT_UNREACTED:V2';
  readonly commentAuthorId: string;
  readonly reactorId: string;
  readonly emoji: string;
  readonly occurredAt: string;
}

export type CommentEvent =
  | CommentCreatedEvent
  | CommentEditedEvent
  | CommentDeletedEvent
  | CommentRestoredEvent
  | CommentReactedEvent
  | CommentUnreactedEvent;

export interface CommentsEventsPort {
  readonly events$: Observable<CommentEvent>;
  readonly connectionState: Signal<CommentsEventsConnectionState>;
}

const disconnected = signal<CommentsEventsConnectionState>('disconnected');

export const COMMENTS_EVENTS = new InjectionToken<CommentsEventsPort>(
  'Comments SSE events',
  {
    providedIn: 'root',
    factory: () => ({
      events$: EMPTY,
      connectionState: disconnected.asReadonly(),
    }),
  },
);
